import { useState, type ReactNode } from "react";
import { ChevronDown, Mail, MessageSquare, Phone, Repeat } from "lucide-react";
import { Button, EmptyState, Icon, Spinner, Tag } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import { useMobileBack } from "../../app/mobileBack.js";
import { openMessageWith } from "../../app/messagePanel.js";
import { roleLabelFrom, useSeatRoles } from "../../app/hooks/useSeatRoles.js";
import { BandAvatar } from "../shared/BandAvatar.js";
import { ActingRoleFullLabel } from "./CrewSeatRow.js";
import { RoleLabel } from "./RoleLabel.js";
import { ActingRolePill, SeatRolePill, type RolePillSkin } from "./CrewProfileModal.js";
import { usePersonProfile } from "./usePersonProfile.js";
import { groupLoyalty, loyaltyLabel, type LoyaltyProgram } from "./travelFormat.js";
import type { components } from "@cntrl/api-client";
import styles from "./MobileContactProfileScreen.module.scss";
import { partnerRelationshipLabel } from "../../app/partnerRelationship.js";

type PersonBandView = components["schemas"]["PersonBandView"];

export interface MobileContactProfileScreenProps {
  /** WHO — from the route param. Identity, permissions and reach are resolved by `usePersonProfile`. */
  personId: string;
  /** History back (supplied by the route wrapper; the screen stays render-context free). Omitted inside the
   *  `ProfileSheet` (ART-264), where the sheet's own dismiss is the way out and the shell header is not ours. */
  onBack?: () => void;
  /** Open a shared band's profile (a reachable band tile) — a history push to `/contacts/band/{id}`. Omitted where
   *  there is no band profile to go to (the roster), which leaves the tiles as plain facts. */
  onOpenBand?: (bandId: string) => void;
  /** Rendered inside the `ProfileSheet` (ART-264): the sheet owns the side gutter, so the screen drops its own. */
  embedded?: boolean;
}

/** Two initials for the header photo fallback, from the resolved display name. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((p) => p[0]).join("") || "?").toUpperCase();
}

/**
 * The MOBILE contact/person profile — the full-screen counterpart of the desktop `CrewProfileModal`
 * (ART-194). Both read the SAME `usePersonProfile` (who this is, what the viewer may see/do), so the
 * permissions and reach cannot drift; only the layout differs. This is the app's standard mobile
 * cards-on-canvas shape: an identity block (a 300px photo CARD, then name · role · location · Message and the
 * seat/acting role badges below it — ART-194) and grey contact chips on the shell's grey canvas, then Skills /
 * Travel info / Artist as white collapsible cards. The shell header carries the "Contacts" section title, so
 * this screen renders none.
 *
 * Render-context free (mount-testable via `renderWithBand`): navigation rides callbacks. Seat EDITING stays
 * on the desktop modal — the mockup is a read-only contact view — so the role is a static label. Travel splits
 * the typed loyalty list into Airlines and Hotels rows (ART-197) via the shared `groupLoyalty`, so this card
 * and the desktop modal cannot draw the split two different ways.
 */

/** The role pickers dressed for this light canvas (the dialog's defaults are for its dark rail). */
const CANVAS_SKIN: RolePillSkin = {
  seat: `${styles.seatPill} ${styles.pillPicker}`,
  acting: `${styles.actingPill} ${styles.pillPicker}`,
  actingGhost: styles.actingPillGhost,
};

export function MobileContactProfileScreen({ personId, onBack, onOpenBand, embedded }: MobileContactProfileScreenProps) {
  const p = usePersonProfile(personId);
  // Back lives in the shell header's leading slot (logo-replacement), not in the screen body. Null in the sheet.
  useMobileBack(onBack ?? null);

  if (p.profile.isLoading || p.profile.isError) {
    return (
      <div className={styles.profile} data-testid="mobile-contact-profile" data-embedded={embedded || undefined}>
        {p.profile.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={22} label="Loading profile…" />
          </div>
        ) : p.profile.error instanceof ApiError && p.profile.error.status === 404 ? (
          <EmptyState title="Profile not available" description="This person isn't visible to you." />
        ) : (
          <EmptyState
            title="Couldn't load this profile"
            description={p.profile.error instanceof Error ? p.profile.error.message : "Please try again."}
            action={<Button onClick={() => void p.profile.refetch()}>Retry</Button>}
          />
        )}
      </div>
    );
  }

  const d = p.profile.data;
  const skills = (d?.skills ?? []).filter((s): s is string => typeof s === "string");
  const email = p.seat?.email ?? null;
  const { airlines, hotels } = groupLoyalty(p.travel.data?.loyaltyPrograms);

  return (
    <div className={styles.profile} data-testid="mobile-contact-profile" data-embedded={embedded || undefined}>
      <header className={styles.head}>
        <div className={styles.phero}>
          {/* A CIRCLE beside the name, not the 300px photo card that led before (ART-264) — the profile now opens
              as a sheet over a chat, and a card that tall left the name below the fold. No photo → initials. */}
          <div className={styles.photo}>
            {p.avatarUrl ? (
              <img className={styles.photoImg} src={p.avatarUrl} alt="" data-photo="" />
            ) : (
              <span className={styles.photoInitials} aria-hidden="true">
                {initialsOf(p.name)}
              </span>
            )}
          </div>
          <div className={styles.ident}>
            <h1 className={styles.name}>{p.name}</h1>
            {/* The role lives in the seat pill below — not restated here as a plain line. */}
            {d?.location && <p className={styles.location}>{d.location}</p>}
            {/* The member's partner (ART-239) — name only, for their band (the server nulls it for anyone else). */}
            {d?.partner && <p className={styles.location}>{partnerRelationshipLabel(d.partnerRelationship) ?? "Partner"} · {d.partner}</p>}
            {/* Contact chips sit in the name column, beside the picture with the name and city (ART-264). */}
            {email && (
              <div className={styles.pills}>
                {email && (
                  <a className={styles.pill} href={`mailto:${email}`} data-selectable>
                    <Icon icon={Mail} size={16} />
                    {email}
                  </a>
                )}
              </div>
            )}
          </div>
          {/* Message, then the phone, as pills on their own row below the picture, running on under the name column
              (product owner, 2026-10-08). Message only when the server says they're reachable
              (`usePersonProfile.canMessage`); the phone is colleague-only (the server nulls it for friend-band reach). */}
          {(p.canMessage || d?.phone) && (
            <div className={styles.actions}>
              {p.canMessage && (
                <button type="button" className={styles.pill} onClick={() => openMessageWith(personId)}>
                  <Icon icon={MessageSquare} size={16} />
                  Message
                </button>
              )}
              {d?.phone && (
                <a className={styles.pill} href={`tel:${d.phone}`} data-selectable>
                  <Icon icon={Phone} size={16} />
                  {d.phone}
                </a>
              )}
            </div>
          )}
        </div>
        {/* Role badges: the standing SEAT (a fact), then the ACTING role for this run (temporary, so the
            louder of the two). A sibling of the name block — not inside `.ident`, where the acting pill would
            wrap. Only when a seat resolves here (a friend-band member has none). Acting is Standard-only and
            conditional — the same `!isPro && actingRole` gate CrewSeatRow uses; the seat pill sits alone
            otherwise. `acting_role` is served on both tiers, so a downgrade brings the pill back over data
            that was there the whole time. */}
        {/* EDITABLE for a viewer who may change this seat (ART-251: "as band member AV I need to click roles and
            change them") — the SAME pickers and the SAME `usePersonProfile` verdicts as the desktop dialog, dressed
            for this light canvas. A viewer who may not edit sees the plain badges, as before. */}
        {p.role != null && (
          <div className={styles.roles}>
            {p.canEditSeatRole && p.seatId ? (
              <SeatRolePill seatId={p.seatId} role={p.role} titleKey={p.titleKey} skin={CANVAS_SKIN} />
            ) : (
              <span className={styles.seatPill} data-testid="seat-pill">
                <RoleLabel role={p.role} titleKey={p.titleKey} owner={p.isOwner} />
              </span>
            )}
            {!p.isPro && p.canEditActing && p.seatId ? (
              <ActingRolePill seatId={p.seatId} actingRole={p.seat?.actingRole ?? null} skin={CANVAS_SKIN} />
            ) : (
              !p.isPro && p.seat?.actingRole && (
                <span className={styles.actingPill} data-testid="acting-pill">
                  <Icon icon={Repeat} size={16} className={styles.actingGlyph} />
                  <ActingRoleFullLabel role={p.seat.actingRole} />
                </span>
              )
            )}
          </div>
        )}

      </header>

      {d?.bio && (
        // A plain, title-less card rather than an expander (ART-264): it sits right under the name, so it needs no
        // label to say what it is, and a bio is short enough that collapsing it hides nothing worth hiding.
        <section className={`${styles.card} ${styles.bioCard}`} aria-label="Bio">
          <p className={styles.bio}>{d.bio}</p>
        </section>
      )}

      {skills.length > 0 && (
        <CollapsibleCard label="Skills" count={skills.length} defaultOpen={false}>
          <div className={styles.chips}>
            {skills.map((sk) => (
              <Tag key={sk} tone="muted">
                {sk}
              </Tag>
            ))}
          </div>
        </CollapsibleCard>
      )}

      {/* Travel is its own money-team-gated read — absent means "not permitted", so no card at all. The typed
          loyalty list splits into Airlines and Hotels rows (ART-197); each membership is a `name · number` line. */}
      {p.travel.data && (
        <CollapsibleCard label="Travel info" defaultOpen>
          {p.travel.data.knownTravelerNumber ||
          p.travel.data.redressNumber ||
          airlines.length > 0 ||
          hotels.length > 0 ? (
            <dl className={styles.travelTable}>
              {p.travel.data.knownTravelerNumber && (
                <TravelRow label="Known Traveler #" value={p.travel.data.knownTravelerNumber} />
              )}
              {p.travel.data.redressNumber && <TravelRow label="Redress #" value={p.travel.data.redressNumber} />}
              {airlines.length > 0 && <TravelRow label="Airlines" value={<LoyaltyLines items={airlines} />} />}
              {hotels.length > 0 && <TravelRow label="Hotels" value={<LoyaltyLines items={hotels} />} />}
            </dl>
          ) : (
            <p className={styles.empty}>Nothing recorded yet.</p>
          )}
        </CollapsibleCard>
      )}

      {/* Payment — the same money-team gated read as Travel (ART-241). '@' shown, stored without it. */}
      {p.payment.data && (
        <CollapsibleCard label="Payment" defaultOpen>
          {p.payment.data.venmoUsername ? (
            <dl className={styles.travelTable}>
              <TravelRow label="Venmo" value={`@${p.payment.data.venmoUsername}`} />
            </dl>
          ) : (
            <p className={styles.empty}>No Venmo on profile.</p>
          )}
        </CollapsibleCard>
      )}

      {p.bandItems.length > 0 && (
        <CollapsibleCard label={p.bandItems.length === 1 ? "Artist" : "Artists"} defaultOpen>
          <ul className={styles.bandStrip}>
            {p.bandItems.map((b) => {
              const reachable = !!b.bandId && (b.bandId === p.activeBandId || p.friendBandIds.has(b.bandId));
              return (
                <ArtistTile
                  key={b.bandId}
                  band={b}
                  onOpen={reachable && onOpenBand ? () => onOpenBand(b.bandId as string) : undefined}
                />
              );
            })}
          </ul>
        </CollapsibleCard>
      )}
    </div>
  );
}

/** A white section card with a collapsible header (label left, optional count + chevron right) — the mockup's
 *  Skills / Travel info / Artist cards. Skills ships collapsed; the rest expanded. */
function CollapsibleCard({
  label,
  count,
  defaultOpen = true,
  children,
}: {
  label: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={styles.card}>
      <button type="button" className={styles.cardHead} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={styles.cardLabel}>{label}</span>
        {count != null && <span className={styles.count}>{count}</span>}
        <Icon icon={ChevronDown} size={20} className={open ? styles.chevOpen : styles.chev} />
      </button>
      {open && <div className={styles.cardBody}>{children}</div>}
    </section>
  );
}

/** One label/value row of the Travel info table (mono label left, bold value right). The value is a node so
 *  the Airlines/Hotels rows can stack a membership per line. */
function TravelRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.travelRow}>
      <dt className={styles.travelLabel}>{label}</dt>
      <dd className={styles.travelValue}>{value}</dd>
    </div>
  );
}

/** A group's memberships, one `name · number` line each (the mockup stacks Airlines/Hotels this way). */
function LoyaltyLines({ items }: { items: LoyaltyProgram[] }) {
  return (
    <>
      {items.map((p, i) => (
        <span key={i} className={styles.loyaltyLine}>
          {loyaltyLabel(p)}
        </span>
      ))}
    </>
  );
}

/**
 * A shared band tile on the LIGHT canvas — the modal's `BandTile` is styled for its DARK rail (paper text),
 * so this is the light-canvas twin: photo + name stacked over role, reachable tiles navigate. An org-reach
 * tile shows the org role from the title; a direct band shows the seat role + AV mark.
 */
function ArtistTile({ band, onOpen }: { band: PersonBandView; onOpen?: () => void }) {
  const seatRoles = useSeatRoles();
  const face = (
    <>
      <BandAvatar bandId={band.bandId} avatarMediaId={band.avatarMediaId} initials={(band.name ?? "?").slice(0, 2).toUpperCase()} size="md" />
      <span className={styles.artistText}>
        <span className={styles.artistName}>{band.name || "Band"}</span>
        {band.access === "org_reach" ? (
          <span className={styles.artistRole}>{roleLabelFrom(seatRoles.data?.orgRoles, band.titleKey)}</span>
        ) : (
          <RoleLabel role={band.role} owner={band.isOwner} className={styles.artistRole} />
        )}
      </span>
    </>
  );
  return (
    <li>
      {onOpen ? (
        <button type="button" className={styles.artistTile} aria-label={`View ${band.name ?? "band"}'s profile`} onClick={onOpen}>
          {face}
        </button>
      ) : (
        <div className={styles.artistTile}>{face}</div>
      )}
    </li>
  );
}
